// ZoomBar.jsx
// Description: This component provides a vertical zoom bar for adjusting the zoom level in a viewer application

// Copyright HS Analysis GmbH, 2020
// Authors: Viktor Eberhardt

// Framework imports
import React, { Component } from "react";
import PropTypes from "prop-types";

// MUI imports
import withStyles from "@mui/styles/withStyles";

// define the component's styling
const styles = () => ({
  root: {
    width: 50,
    position: "absolute",
    bottom: 50,
    right: 5,
    opacity: 0.8,
  },
  verticalBar: {
    position: "absolute",
    top: -15,
    left: "50%",
    marginLeft: -2,
    width: 4,
    background: "#0673C1",
  },
  fab: {
    position: "absolute",
    marginTop: -15,
    marginLeft: -15,
    left: "50%",
    width: 30,
    height: 30,
    background: "white",
    color: "#0673C1",
    border: "2px solid #0673C1",
    borderRadius: "50%",
    fontSize: "12px",
    fontWeight: "bold",
    lineHeight: "25px",
    textAlign: "center",
    cursor: "pointer",
  },
});
const marks = [
  {
    value: 100,
    level: 40,
  },
  {
    value: 75,
    level: 20,
  },
  {
    value: 50,
    level: 10,
  },
  {
    value: 30,
    level: 4,
  },
  {
    value: 15,
    level: 2,
  },
  {
    value: 0,
    level: 1,
  },
];

class ZoomBar extends Component {
  /**
   * Determines if the given index corresponds to the selected mark based on the objective factor.
   *
   * The function calculates the selected index by iterating through the `marks` array
   * and finding the mark with the highest level that is less than or equal to the objective factor.
   * It then checks if the provided index matches the calculated selected index.
   *
   * @param {number} idx - The index to check for selection.
   * @returns {boolean} - Returns `true` if the given index is the selected one, otherwise `false`.
   */
  isSelected(idx) {
    const factor = this.getObjectiveFactor();
    let selectedIndex = marks.length - 1; // default to the last mark (1x)
    let maxLevel = -Infinity;
    marks.forEach((mark, i) => {
      if (mark.level <= factor && mark.level > maxLevel) {
        maxLevel = mark.level;
        selectedIndex = i;
      }
    });
    return selectedIndex === idx;
  }

  /**
   * Computes the zoom factor corresponding to the current scale.
   *
   * @returns {number} The factor such that
   *                   scaleTarget = (wsiPixelSize / pixelSize1x) * factor,
   *                   where scaleTarget is this.getScale().
   */
  getObjectiveFactor() {
    const wsiPixelSize = this.props.ome.physicalSizeX; // e.g. 4.0 µm at 1×
    const pixelSize1x = 4 / 1_000_000; // 4 µm → meters (4 × 10⁻⁶ m)

    const scaleTarget = this.props.zoom; // current scale in the same units
    const factor = scaleTarget / (wsiPixelSize / pixelSize1x);
    return Math.round(factor);
  }

  render() {
    const { classes, clickable, height = 300 } = this.props;
    return (
      <React.Fragment>
        <div
          className={classes.root}
          style={{
            pointerEvents: clickable ? "all" : "none",
            zIndex: 9999,
            height: height,
          }}
        >
          <div className={classes.verticalBar} style={{ height: "100%" }} />

          {marks.map((mark, idx) => {
            return (
              <div key={idx} className={classes.markButton}>
                <div
                  style={{
                    background: this.isSelected(idx) ? "#0673C1" : "white",
                    color: this.isSelected(idx) ? "white" : "#0673C1",
                    bottom: mark.value + "%",
                  }}
                  className={classes.fab}
                  onClick={() => this.props.changeValue(mark.level)}
                >
                  {this.getObjectiveFactor() === mark.level
                    ? this.getObjectiveFactor() + "x"
                    : mark.level + "x"}
                </div>
              </div>
            );
          })}
        </div>
      </React.Fragment>
    );
  }
}

// define the component's interface
ZoomBar.propTypes = {
  classes: PropTypes.object.isRequired,
  ome: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  clickable: PropTypes.bool,
  changeValue: PropTypes.func,
  height: PropTypes.number,
};

ZoomBar.defaultProps = {
  height: 300,
};

export default withStyles(styles)(ZoomBar);
